import React, { FC, useEffect } from 'react';
import useTitleBarSelect from '../../../../../hooks/useTitleBarSelect';
import CisTable, { ExposureType } from './table/cisTable.component';
import CisMaps from './maps/CisMaps';
import { GeneralOverviewPageRenderedComponentProps } from '../../../../layout/general/SimpleGeneralPageWrapper';
import { useDispatch } from 'react-redux';
import { Select } from '../../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../../types/redux/pages/pageTypes';
import { useMancoCisPageModel } from '../../../../../api/rs-hg/manco';
import { QueryBoundary } from '../../../../utilityComponents/QueryBoundary';
import { CisServerData } from './cis.data';

const MancoCis: FC<GeneralOverviewPageRenderedComponentProps> = ({
  positionDate,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Exposure Type: ',
        titleBarKey: 'exposureType',
        values: [
          {
            label: 'Net Exposure, Base Currency',
            value: 'netExposureBaseCurrency',
          },
          {
            label: 'Net Exposure, % NAV',
            value: 'netExposurePcNav',
          },
        ],
        displayOnOverviewPage: true,
        aValueIsAlwaysSelected: true,
        currentValue: 'netExposureBaseCurrency',
      },
      {
        titleBarTitle: 'Visualisation: ',
        titleBarKey: 'selectVisualisation',
        values: [
          {
            label: 'Position Table',
            value: 'positionTable',
          },
          {
            label: 'Exposure Graphics',
            value: 'exposureGraphics',
          },
        ],
        displayOnOverviewPage: true,
        aValueIsAlwaysSelected: true,
        currentValue: 'positionTable',
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent('exposureType', REMOVE_SELECT_FROM_TITLEBAR),
      );
      dispatch(
        removeTitleBarComponent(
          'selectVisualisation',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [dispatch]);

  const exposureType = useTitleBarSelect('exposureType');
  const selectedVisualisation = useTitleBarSelect('selectVisualisation');

  const cisQuery = useMancoCisPageModel(positionDate);
  const cisData = (cisQuery.data?.data ?? null) as CisServerData | null;

  return (
    <QueryBoundary<CisServerData>
      isLoading={cisQuery.isLoading}
      isFetching={cisQuery.isFetching}
      isError={cisQuery.isError}
      error={cisQuery.error}
      data={cisData}
      loadingMessages={[
        'Loading Manco CIS Data...',
        'This may take a few moments...',
      ]}
      errorMessage="No In-Scope or Available Data"
      noDataMessage="No In-Scope or Available Data"
    >
      {(report) =>
        selectedVisualisation === 'positionTable' ? (
          <CisTable
            cisData={report}
            isFetching={cisQuery.isFetching}
            selectedExposureType={
              (exposureType as ExposureType) || 'netExposureBaseCurrency'
            }
          />
        ) : (
          <CisMaps
            cisData={report}
            isFetching={cisQuery.isFetching}
            selectedExposureType={
              (exposureType as ExposureType) || 'netExposureBaseCurrency'
            }
          />
        )
      }
    </QueryBoundary>
  );
};

export default MancoCis;
